import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Stack from "@mui/material/Stack";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import Popover from "@mui/material/Popover";
import { useCallback, useState } from "react";

import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Link as RouterLink, useLocation, Location } from "react-router";
import { useIsAdmin, useTenant } from "hooks/settings";
import { Typography } from "@mui/material";
import { useTenantDataOptional } from "contexts/tenant";
import {
  useCranePagesEnabled,
  useHasPermission,
  useHeatOverridesEnabled,
} from "hooks/permissions";
import { useCanDeploy } from "hooks/useCanDeploy";
import { useTenantMenuItems } from "../menuItems/tenantMenuItems";
import { useMultiTenantMenuItems } from "../menuItems/multiTenantMenuItems";
import { MenuItem } from "../menuItems/types";
import { useTenantTranslation } from "hooks/formatters";

const MenuItemComponent = ({
  item,
  location,
  isChild = false,
  collapsed = false,
}: {
  item: MenuItem;
  location: Location;
  isChild?: boolean;
  collapsed?: boolean;
}) => {
  const tenant = useTenant();
  const prefix = tenant ? `/${tenant}` : "";
  const withPrefix = useCallback(
    (path: string) => (prefix.length > 0 ? `${prefix}/${path}` : path),
    [prefix]
  );
  const { t } = useTenantTranslation();
  const isAdmin = useIsAdmin();
  const canDeploy = useCanDeploy();
  const heatOverridesAreEnabled = useHeatOverridesEnabled();
  const cranePagesAreEnabled = useCranePagesEnabled();
  const { feature_flags: tenantFeatureFlags } = useTenantDataOptional();

  const hasPermission = useHasPermission();
  const canAccessItem = (menuItem: MenuItem) =>
    (menuItem.permissionsToCheck?.some(hasPermission) ?? true) &&
    (menuItem.requiredPermissions?.every(hasPermission) ?? true) &&
    (!menuItem.requireAdmin || isAdmin) &&
    (!menuItem.requireDeploy || canDeploy) &&
    (!menuItem.requireHeatOverrides || heatOverridesAreEnabled) &&
    (!menuItem.requireCranePages || cranePagesAreEnabled) &&
    (!menuItem.featureFlag ||
      tenantFeatureFlags?.includes(menuItem.featureFlag));

  const [expanded, setExpanded] = useState(() => {
    if (!item.children || item.children.length === 0) return false;

    if (item.expandedByDefault !== undefined) {
      return item.expandedByDefault;
    }

    return item.children.some(
      (child) =>
        location.pathname === withPrefix(child.path) ||
        location.pathname === `${withPrefix(child.path)}/`
    );
  });

  const hasChildren = item.children && item.children.length > 0;
  const filteredChildren =
    hasChildren && item.children ? item.children.filter(canAccessItem) : [];

  const shouldShowItem =
    canAccessItem(item) && (!hasChildren || filteredChildren.length > 0);

  const isSelected =
    location.pathname === withPrefix(item.path) ||
    location.pathname === `${withPrefix(item.path)}/` ||
    (location.pathname === prefix && item.path === "");

  const hasSelectedChild =
    hasChildren &&
    filteredChildren.some(
      (child) =>
        location.pathname === withPrefix(child.path) ||
        location.pathname === `${withPrefix(child.path)}/`
    );

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const popoverOpen = Boolean(popoverAnchor);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (hasChildren && filteredChildren.length > 0) {
      if (collapsed) {
        setPopoverAnchor(event.currentTarget);
      } else {
        setExpanded(!expanded);
      }
    }
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
  };

  const shouldNavigate = !hasChildren;

  const listItemContent = (
    <ListItem key={item.path} disablePadding sx={{ display: "block" }}>
      <ListItemButton
        selected={isSelected || hasSelectedChild}
        onClick={
          hasChildren && filteredChildren.length > 0 ? handleClick : undefined
        }
        sx={{
          pl: { xs: 0, md: isChild ? 4 : 1.5 },
          overflow: "hidden",
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
        <Typography
          sx={{
            flexGrow: 1,
            minWidth: 0,
            fontWeight: 500,
            fontSize: isChild ? "0.875rem" : "1rem",
            lineHeight: 1.5,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: collapsed ? "clip" : "ellipsis",
            opacity: collapsed ? 0 : 1,
            transition: "opacity 0.3s ease",
          }}
        >
          {item.label ?? t(item.i18nKey)}
        </Typography>
        {hasChildren && filteredChildren.length > 0 ? (
          <ListItemIcon
            sx={{
              minWidth: 24,
              flexShrink: 0,
              mr: -1,
              opacity: collapsed ? 0 : 1,
              transition: "opacity 0.3s ease",
            }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemIcon>
        ) : null}
      </ListItemButton>
    </ListItem>
  );

  const wrappedContent = (
    <Tooltip
      title={collapsed && !isChild ? (item.label ?? t(item.i18nKey)) : ""}
      placement="right"
      arrow
    >
      {listItemContent}
    </Tooltip>
  );

  if (!shouldShowItem) {
    return null;
  }
  return (
    <>
      {shouldNavigate ? (
        <RouterLink
          to={withPrefix(item.path)}
          key={item.path}
          style={{
            textDecoration: "none",
            color: "inherit",
          }}
        >
          {wrappedContent}
        </RouterLink>
      ) : (
        wrappedContent
      )}
      {hasChildren && filteredChildren.length > 0 ? (
        <Collapse in={expanded && !collapsed} timeout={300} unmountOnExit>
          <List component="div" disablePadding>
            {filteredChildren.map((child) => (
              <MenuItemComponent
                key={child.path}
                item={child}
                location={location}
                isChild
                collapsed={collapsed}
              />
            ))}
          </List>
        </Collapse>
      ) : null}
      <Popover
        open={popoverOpen}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        sx={{
          ml: 1,
        }}
      >
        <List dense sx={{ minWidth: 200 }}>
          {filteredChildren.map((child) => (
            <RouterLink
              key={child.path}
              to={withPrefix(child.path)}
              style={{
                textDecoration: "none",
                color: "inherit",
              }}
              onClick={handlePopoverClose}
            >
              <ListItem disablePadding>
                <ListItemButton>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {child.icon}
                  </ListItemIcon>
                  <Typography sx={{ fontSize: "0.875rem" }}>
                    {child.label ?? t(child.i18nKey)}
                  </Typography>
                </ListItemButton>
              </ListItem>
            </RouterLink>
          ))}
        </List>
      </Popover>
    </>
  );
};

export const MenuContent = ({ collapsed = false }: { collapsed?: boolean }) => {
  const tenant = useTenant();
  const location = useLocation();
  const multiTenantItems = useMultiTenantMenuItems();
  const tenantMenuItems = useTenantMenuItems();
  const items = tenant ? tenantMenuItems : multiTenantItems;

  return (
    <Stack
      sx={{
        flexGrow: 1,
        p: 1,
        justifyContent: "space-between",
        overflow: "auto",
      }}
    >
      <List dense>
        {items.map((item) => (
          <MenuItemComponent
            key={item.path}
            item={item}
            location={location}
            collapsed={collapsed}
          />
        ))}
      </List>
    </Stack>
  );
};
